/**
 * AgentChatController — lightweight helper that determines whether a user's
 * chat input should be routed to the Visual Editor Agent (remote) or handled
 * locally by the existing slash-command / quick-pattern / AI-backend flow.
 *
 * Phase 4: Functions are async to support LLM-based Tier 2 classification.
 * Tier 1 (9 regex patterns) returns instantly. Tier 2 uses LLM or regex fallback.
 *
 * Phase 4a-3: Tier 2 pattern arrays moved into IntentClassifier (internal).
 * Only Tier 1 patterns remain exported here for consumers that need them.
 */

import { IntentClassifier, type RoutingClassificationResult, type RoutingClassificationContext } from './IntentClassifier';

// ─── Tier 1 Pattern Arrays (kept permanently — syntactically stable) ────────

// Patterns that should ALWAYS stay local (slash commands, undo, quick patterns)
export const LOCAL_ONLY_PATTERNS = [
  /^\//, // slash commands
  /^(undo|redo|repeat)\b/i,
  /^(ctrl|cmd|command)\s*\+?\s*z/i,
];

// Quick property patterns that should stay local when a component is selected
export const QUICK_PROPERTY_PATTERNS = [
  /^(make it|set|change)\s+(red|blue|green|bigger|smaller|bold|italic|larger|darker|lighter)/i,
  /^#[0-9a-fA-F]{3,8}$/,
  /^\d+\s*px$/,
  /^(?:(bg|background|color|font|size|padding|margin|border|radius|opacity|gap)\s|(?:hide|remove|delete)\s+(?:the\s+)?border\b|no\s+border\b)/i,
];

// Patterns for form creation (route to visual editor agent)
export const FORM_PATTERNS = [
  /\b(create|build|make|generate)\b.*\b(form|input form|data entry)\b/i,
  /\b(form)\b.*\b(for|from|based on)\b.*\b(schema|data)\b/i,
];

// ─── Exported Interfaces & Types ─────────────────────────────────────────────

export interface RoutingContext {
  hasSelectedComponent: boolean;
  hasSelectedComponentType?: string;
  agentAvailable: boolean;
  hasSessionContext?: boolean;
  preferUserAgentContinuation?: boolean;
  tradeScope?: string | null;
}

export type RouteDecision = 'local' | 'visual-editor' | 'visual-editor-stream' | 'data-workflow' | 'user-agent';

// Re-export for consumers that need the full result
export type { RoutingClassificationResult, RoutingClassificationContext };

// ─── Async Routing Functions ─────────────────────────────────────────────────

/**
 * Full classification with metadata. Prefer this over the legacy wrappers
 * when you need complexity, follow-up, or breakpoint information.
 */
export async function classifyRoute(
  input: string,
  context: RoutingContext,
): Promise<RoutingClassificationResult> {
  const classifier = IntentClassifier.getInstance();
  return classifier.classify(input, {
    hasSelectedComponent: context.hasSelectedComponent,
    hasSelectedComponentType: context.hasSelectedComponentType,
    agentAvailable: context.agentAvailable,
    hasSessionContext: context.hasSessionContext ?? false,
    preferUserAgentContinuation: context.preferUserAgentContinuation ?? false,
  });
}

/**
 * Returns true if the input should be routed to the Visual Editor Agent.
 * Returns false for anything that should stay in the existing local flow.
 *
 * Async wrapper — delegates to IntentClassifier.
 */
export async function shouldRouteToVisualEditor(
  input: string,
  context: RoutingContext,
): Promise<boolean> {
  const result = await classifyRoute(input, context);
  return result.route === 'visual-editor-stream';
}

/**
 * Determine the routing decision with streaming preference.
 * Uses streaming for visual editor requests, non-streaming for everything else.
 *
 * Async wrapper — delegates to IntentClassifier.
 */
export async function routeToStream(
  input: string,
  context: RoutingContext,
): Promise<RouteDecision> {
  const result = await classifyRoute(input, context);
  if (result.route === 'visual-editor-stream') return 'visual-editor-stream';
  if (result.route === 'data-workflow') return 'data-workflow';
  if (result.route === 'user-agent') return 'user-agent';
  return 'local';
}
